import {
  Account,
  ActivityBaseModel,
  SoftwareLicense,
  UserProfilePublic,
  PriceInformation,
} from '@/client'
import { AccountStore } from '@/stores/account/account.store'
import { ApplicationStore } from '@/stores/application/application.store'
import { logError } from '@/utils/errors'

async function getApplicationNameById(id: string, sub_account?: string) {
  const app = await ApplicationStore.getApplicationById(id, undefined, {
    refetchFromApplication: false,
  })

  if (app === null) throw new Error(`Application not found for ID: ${id}`)
  if (app?.sso_provider && sub_account) {
    return sub_account
  }

  return app.software_name
}

export async function getI18nArgs(
  activity: ActivityBaseModel,
  users: Account[],
  softwareLicenses: SoftwareLicense[],
  contracts: PriceInformation[],
  userProfile?: UserProfilePublic
) {
  let textName: string | undefined
  let applicationName: string | undefined
  let importCount: number | undefined

  const crud_id =
    activity.data.created_id ??
    activity.data.deleted_id ??
    activity.data.updated_id

  switch (activity.action) {
    case 'licence.import': {
      const importedIds = activity.data.imported_ids ?? []
      importCount = importedIds.length
      break
    }
    case 'licence.create':
    case 'licence.update':
    case 'licence.update_resend_invitation':
    case 'licence.update_assign_account':
    case 'licence.update_unassign_account':
    case 'licence.delete':
      if (!crud_id) break
      try {
        const account = await AccountStore.getAccountById(crud_id)
        if (!account) {
          textName = '...'
          applicationName = '...'
          break
        }
        const licenseAccount = users.find((u) => u._id == account.account_id)
        textName = licenseAccount?.name || account.email
        if (account.software_id) {
          applicationName = await getApplicationNameById(
            account.software_id,
            account.sub_account_id ?? undefined
          )
        }
      } catch (error) {
        logError(
          error,
          'Failed to resolve licence activity details',
          { crudId: crud_id, action: activity.action },
          { op: 'activity.getI18nArgs' }
        )
        textName = '...'
        applicationName = applicationName ?? '...'
      }

      break

    case 'account.create':
    case 'account.create_from_alias':
    case 'account.update':
    case 'account.update_user_email':
    case 'account.delete':
    case 'account.deactivate':
    case 'account.password.change':
    case 'account.password.reset':
    case 'account.alias.create':
    case 'account.alias.update':
    case 'account.alias.merge':
    case 'account.alias.delete': {
      const user = users.find((u) => u._id == crud_id)
      textName = user?.name ?? user?.email
      const needsAccountFetch =
        !textName ||
        (activity.action === 'account.update_user_email' && !user?.email)

      if (needsAccountFetch && crud_id) {
        try {
          const account = await AccountStore.getAccountById(crud_id)
          if (account) {
            textName = account.name ?? account.email ?? textName
            if (
              activity.action === 'account.update_user_email' &&
              account.email
            ) {
              textName = account.email
            }
          } else {
            textName = '...'
          }
        } catch (error) {
          logError(
            error,
            'Failed to resolve account activity details',
            { crudId: crud_id, action: activity.action },
            { op: 'activity.getI18nArgs' }
          )
          textName = '...'
        }
      } else if (
        activity.action === 'account.update_user_email' &&
        user?.email
      ) {
        textName = user.email
      }
      break
    }
    case 'login.password':
      break
    case 'software.create':
    case 'software.create_oauth':
    case 'software.re_authorize':
    case 'software.re_authorize_oauth':
    case 'software.update':
    case 'software.delete':
      if (crud_id) {
        applicationName = await getApplicationNameById(crud_id)
      }
      break
    case 'software.update_software_license.create':
    case 'software.update_software_license.update':
    case 'software.update_software_license.delete':
    case 'software_license.create':
    case 'software_license.update':
    case 'software_license.delete': {
      const license = softwareLicenses.find((s) => s._id == crud_id)
      if (license?.software_id) {
        applicationName = await getApplicationNameById(
          license.software_id,
          license.sub_account_id ?? undefined
        )
      }
      break
    }
    case 'software_license_price_information.create':
    case 'software_license_price_information.update':
    case 'software_license_price_information.delete': {
      const contract = contracts.find((s) => s._id == crud_id)
      const softwareLicense = softwareLicenses.find(
        (s) => s._id == contract?.software_license_id
      )
      if (softwareLicense?.software_id) {
        applicationName = await getApplicationNameById(
          softwareLicense.software_id,
          softwareLicense.sub_account_id ?? undefined
        )
      }
      break
    }
  }

  const fallback = (value?: string | null) =>
    value && value.toString().trim().length > 0 ? value : '...'

  return {
    email: fallback(textName),
    applicationName: fallback(applicationName),
    softwareName: fallback(applicationName),
    importCount: importCount ?? 0,
    updated_licences: '',
    performedBy: fallback(userProfile?.display_name),
  }
}
